// The right pane of the immersive Git client when a commit is selected in the
// center graph (design 2.4.2): full commit detail plus its changed files. It
// fetches `git.commitDetail(root, hash)` for the selected commit (cancelling a
// superseded fetch), then renders the subject, body, author + relative date,
// the short/full hash with a copy affordance, parent short hashes, ref pills
// (mirroring the graph's pill style), and a Changed-files list. Each file row
// shows a tone-colored status icon, the path (dir greyed, filename emphasized,
// `orig -> path` for renames) and right-aligned +/- line counts (or a "binary"
// badge). Rows are non-interactive for now (diff view is a later slice). All
// `git` access is guarded because the capability is optional (desktop-only).

import {
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import type { GitApi, GitCommitDetail, GitFileChange } from '@slate/core';
import { Check, Copy } from 'lucide-react';
import { basename, dirOf } from '../../fs/containment';
import { relativeTime } from '../relativeTime';
import { gitStatusIcon } from '../gitStatusIcon';
import { CommitBodyMarkdown } from '../CommitBodyMarkdown';

export interface GitCommitDetailViewProps {
  /** The repo to read from, or null when no repo resolves. */
  root: string | null;
  /** The platform GitApi (guarded; undefined when unavailable). */
  git: GitApi | undefined;
  /** The full hash of the selected commit. */
  hash: string;
}

interface RefPill {
  label: string;
  kind: 'head' | 'tag' | 'branch';
}

/** Classify one `%D` ref decoration into a pill (label + style variant). */
function classifyRef(ref: string): RefPill {
  if (ref.startsWith('tag: ')) return { label: ref.slice(5), kind: 'tag' };
  if (ref === 'HEAD') return { label: 'HEAD', kind: 'head' };
  const arrow = ref.indexOf(' -> ');
  const name = arrow >= 0 ? ref.slice(arrow + 4) : ref;
  return { label: name, kind: arrow >= 0 ? 'head' : 'branch' };
}

/** A single changed-file row: status icon + path + line counts (or binary badge). */
function FileRow({ file }: { file: GitFileChange }): ReactElement {
  const { Icon, tone, label } = gitStatusIcon(file.status);
  const dir = dirOf(file.path);
  const binary = file.added === -1;
  const title =
    file.origPath != null ? `${label}: ${file.origPath} -> ${file.path}` : `${label}: ${file.path}`;
  return (
    <li className="mp-git-detail__file" title="Diff coming soon">
      <Icon
        size={13}
        aria-hidden
        className={`mp-git-detail__file-icon mp-git-status--${tone}`}
      />
      <span className="mp-git-detail__file-path" title={title}>
        {file.origPath != null && (
          <>
            <span className="mp-git-detail__file-orig">
              {dirOf(file.origPath) && (
                <span className="mp-git-detail__file-dir">{dirOf(file.origPath)}/</span>
              )}
              <span className="mp-git-detail__file-name">{basename(file.origPath)}</span>
            </span>
            <span className="mp-git-detail__file-arrow" aria-hidden>
              {' → '}
            </span>
          </>
        )}
        {dir && <span className="mp-git-detail__file-dir">{dir}/</span>}
        <span className="mp-git-detail__file-name">{basename(file.path)}</span>
      </span>
      {binary ? (
        <span className="mp-git-detail__file-binary">binary</span>
      ) : (
        <span className="mp-git-detail__file-counts">
          {file.added > 0 && <span className="mp-git-detail__added">+{file.added}</span>}
          {file.deleted > 0 && <span className="mp-git-detail__deleted">-{file.deleted}</span>}
        </span>
      )}
    </li>
  );
}

export function GitCommitDetailView({
  root,
  git,
  hash,
}: GitCommitDetailViewProps): ReactElement {
  const [detail, setDetail] = useState<GitCommitDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!root || !git) {
      setDetail(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCopied(false);
    void (async () => {
      try {
        const d = await git.commitDetail(root, hash);
        if (cancelled) return;
        setDetail(d);
      } catch (e) {
        if (cancelled) return;
        setDetail(null);
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [root, git, hash]);

  const copyHash = (): void => {
    if (!detail) return;
    void navigator.clipboard?.writeText(detail.hash);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  if (error) {
    return (
      <div className="mp-git-detail">
        <p className="mp-git__error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="mp-git-detail">
        <p className="mp-git__clean">{loading ? 'Loading commit…' : 'No commit.'}</p>
      </div>
    );
  }

  const pills = detail.refs.map(classifyRef);
  const absoluteDate = new Date(detail.date * 1000).toLocaleString();

  return (
    <div className="mp-git-detail">
      <header className="mp-git-detail__header">
        <h2 className="mp-git-detail__subject">{detail.subject}</h2>
        {detail.body && <CommitBodyMarkdown body={detail.body} />}
      </header>

      {pills.length > 0 && (
        <div className="mp-git-detail__refs">
          {pills.map((p, i) => (
            <span
              key={`${p.kind}:${p.label}:${i}`}
              className="mp-git-detail__pill"
              data-kind={p.kind}
            >
              {p.label}
            </span>
          ))}
        </div>
      )}

      <dl className="mp-git-detail__meta">
        <div className="mp-git-detail__meta-row">
          <dt className="mp-git-detail__meta-label">Author</dt>
          <dd className="mp-git-detail__meta-value" title={`${detail.author} <${detail.email}>`}>
            {detail.author}
          </dd>
        </div>
        <div className="mp-git-detail__meta-row">
          <dt className="mp-git-detail__meta-label">Date</dt>
          <dd className="mp-git-detail__meta-value">
            <time dateTime={new Date(detail.date * 1000).toISOString()} title={absoluteDate}>
              {relativeTime(detail.date)}
            </time>
          </dd>
        </div>
        <div className="mp-git-detail__meta-row">
          <dt className="mp-git-detail__meta-label">Commit</dt>
          <dd className="mp-git-detail__meta-value mp-git-detail__hashes">
            <code className="mp-git-detail__hash" title={detail.hash}>
              {detail.short}
            </code>
            <code className="mp-git-detail__hash mp-git-detail__hash--full">{detail.hash}</code>
            <button
              type="button"
              className="mp-iconbtn mp-git-detail__copy"
              aria-label="Copy commit hash"
              title={copied ? 'Copied' : 'Copy commit hash'}
              onClick={copyHash}
            >
              {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
            </button>
          </dd>
        </div>
        {detail.parents.length > 0 && (
          <div className="mp-git-detail__meta-row">
            <dt className="mp-git-detail__meta-label">
              {detail.parents.length > 1 ? 'Parents' : 'Parent'}
            </dt>
            <dd className="mp-git-detail__meta-value mp-git-detail__parents">
              {detail.parents.map((p) => (
                <code key={p} className="mp-git-detail__hash" title={p}>
                  {p.slice(0, 7)}
                </code>
              ))}
            </dd>
          </div>
        )}
      </dl>

      <section className="mp-git-detail__files" aria-label="Changed files">
        <h3 className="mp-git-detail__files-title">
          <span>Changed files</span>
          <span className="mp-git-detail__files-count">{detail.files.length}</span>
        </h3>
        {detail.files.length === 0 ? (
          <p className="mp-git__clean">No file changes.</p>
        ) : (
          <ul className="mp-git-detail__file-list">
            {detail.files.map((f) => (
              <FileRow key={f.path} file={f} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
